import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'

/**
 * Hook to enforce language override from admin settings
 * Checks if the user has a forced language setting and applies it
 */
export function useLanguageOverride() {
  const { i18n } = useTranslation()

  useEffect(() => {
    const abortController = new AbortController()

    async function checkLanguageOverride() {
      try {
        // Add small delay to let AuthContext initialize first
        await new Promise((resolve) => setTimeout(resolve, 150))

        if (abortController.signal.aborted) return

        const {
          data: { user },
        } = await supabase.auth.getUser()

        if (!user || abortController.signal.aborted) return

        // Get account_id first, then fetch language_override from user_preferences
        const { data: account } = await supabase
          .from('accounts')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle()

        if (!account || abortController.signal.aborted) return

        const { data: preferences } = await supabase
          .from('user_preferences')
          .select('language_override')
          .eq('account_id', account.id)
          .maybeSingle()

        if (abortController.signal.aborted) return

        if (preferences?.language_override) {
          // Admin has forced a language - override user preference
          if (i18n.language !== preferences.language_override) {
            await i18n.changeLanguage(preferences.language_override)
          }
        }
      } catch (error) {
        // Ignore AbortError - it's expected during cleanup
        if (error instanceof Error && error.name === 'AbortError') return
        console.error('Error checking language override:', error)
      }
    }

    checkLanguageOverride()

    return () => {
      abortController.abort()
    }
  }, [i18n])
}
